import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { accountingDb, fieldDb, sharedDb, supabase } from '@/lib/supabase';
import { formatErr } from '@/utils/format-error';
import type {
  Extraction,
  ExtractedItem,
  ExtractionStatus,
  RollbackHistoryEntry,
} from '@/types';
// 注:utils/spans の SPAN_COLORS / segmentText / LegendItem は ADR-0008 で
// 原文ベタ表示を廃止したため一旦未使用。将来の明細ハイライト等で再利用予定。

interface ProfitRow {
  extraction_id: string;
  ordered_quantity: number;
  ordered_unit: string | null;
  ordered_total_jpy: number;
  consumed_sqm: number;
  measurement_line_count: number;
}

interface MeasurementSetRow {
  id: string;
  received_at: string;
  raw_text: string;
  lines: {
    id: string;
    height_cm: number;
    column_count: number;
    area_sqm: number;
    section_index: number;
    room_label: string | null;
    part_name: string | null;
  }[];
}

const CLOTH_WIDTH_M = 0.9;
// import { segmentText, SPAN_COLORS } from '@/utils/spans'; // ADR-0008 で未使用

const STATUS_LABEL: Record<ExtractionStatus, string> = {
  draft: '仮案件',
  confirmed: '確定済',
  rolled_back: '巻き戻し済',
  sent_to_provider: '板送信済',
  archived: 'アーカイブ',
};

const STATUS_COLOR: Record<ExtractionStatus, string> = {
  draft: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  rolled_back: 'bg-rose-100 text-rose-800',
  sent_to_provider: 'bg-sky-100 text-sky-800',
  archived: 'bg-slate-100 text-slate-500',
};

export function ExtractionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [ex, setEx] = useState<Extraction | null>(null);
  const [items, setItems] = useState<ExtractedItem[]>([]);
  const [profit, setProfit] = useState<ProfitRow | null>(null);
  const [measurementSets, setMeasurementSets] = useState<MeasurementSetRow[]>([]);
  const [partnerUnitOverride, setPartnerUnitOverride] = useState<Record<string, string> | null>(null);
  const [pdfOrder, setPdfOrder] = useState<{ storage_path: string; original_file_name: string | null } | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const [
        { data: exData },
        { data: itemsData },
        { data: profitData },
        { data: msData },
        { data: pdfData },
      ] = await Promise.all([
        fieldDb().from('extractions').select('*').eq('id', id).maybeSingle(),
        fieldDb().from('extracted_items').select('*').eq('extraction_id', id).order('display_order'),
        fieldDb().from('profit_tracking').select('*').eq('extraction_id', id).maybeSingle(),
        fieldDb()
          .from('measurements')
          .select('id, received_at, raw_text')
          .eq('extraction_id', id)
          .order('received_at'),
        accountingDb()
          .from('pdf_orders')
          .select('storage_path, original_file_name')
          .eq('extraction_id', id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      setEx(exData as unknown as Extraction | null);
      setItems((itemsData ?? []) as unknown as ExtractedItem[]);
      setProfit((profitData ?? null) as ProfitRow | null);
      setPdfOrder((pdfData ?? null) as { storage_path: string; original_file_name: string | null } | null);

      // measurement_lines を 部位名(work_parts.name)込みで fetch
      const measArr = (msData ?? []) as { id: string; received_at: string; raw_text: string }[];
      if (measArr.length > 0) {
        const { data: linesData } = await fieldDb()
          .from('measurement_lines')
          .select(
            'id, measurement_id, height_cm, column_count, area_sqm, section_index, room_label, display_order, location_id',
          )
          .in(
            'measurement_id',
            measArr.map((m) => m.id),
          )
          .order('display_order');
        const partIds = Array.from(
          new Set(
            ((linesData ?? []) as { location_id: string | null }[])
              .map((l) => l.location_id)
              .filter((v): v is string => !!v),
          ),
        );
        const partsMap: Record<string, string> = {};
        if (partIds.length > 0) {
          const { data: parts } = await sharedDb()
            .from('work_parts')
            .select('id, name')
            .in('id', partIds);
          for (const p of (parts ?? []) as { id: string; name: string }[]) {
            partsMap[p.id] = p.name;
          }
        }
        const grouped = measArr.map((m) => ({
          ...m,
          lines: (
            (linesData ?? []) as {
              id: string;
              measurement_id: string;
              height_cm: number;
              column_count: number;
              area_sqm: number;
              section_index: number;
              room_label: string | null;
              location_id: string | null;
            }[]
          )
            .filter((l) => l.measurement_id === m.id)
            .map((l) => ({
              id: l.id,
              height_cm: l.height_cm,
              column_count: l.column_count,
              area_sqm: l.area_sqm,
              section_index: l.section_index,
              room_label: l.room_label,
              part_name: l.location_id ? partsMap[l.location_id] ?? null : null,
            })),
        }));
        setMeasurementSets(grouped);
      } else {
        setMeasurementSets([]);
      }

      setLoading(false);
    })();
  }, [id]);

  // 取引先別単位 override(アクシス建設 = m 単位・原則 13)
  useEffect(() => {
    if (!ex?.partner_id) {
      setPartnerUnitOverride(null);
      return;
    }
    (async () => {
      const { data } = await sharedDb()
        .from('partners')
        .select('unit_preferences_override')
        .eq('id', ex.partner_id)
        .maybeSingle();
      setPartnerUnitOverride(
        (data?.unit_preferences_override ?? null) as Record<string, string> | null,
      );
    })();
  }, [ex?.partner_id]);

  if (loading) return <div className="p-6 text-sm text-slate-500">読み込み中...</div>;
  if (!ex) return <div className="p-6 text-sm text-slate-500">見つかりません</div>;

  // 「PDF を開く」(ADR-0008): Storage の signed URL を生成 → 別タブ表示
  const handleOpenPdf = async () => {
    if (!pdfOrder?.storage_path || pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const { data, error } = await supabase.storage
        .from('accounting-pdfs')
        .createSignedUrl(pdfOrder.storage_path, 3600);
      if (error || !data?.signedUrl) throw error ?? new Error('failed to sign url');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setPdfError(formatErr(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const toggleItem = async (item: ExtractedItem) => {
    setSaving(true);
    const next = !item.checked;
    await fieldDb()
      .from('extracted_items')
      .update({ checked: next, checked_at: next ? new Date().toISOString() : null })
      .eq('id', item.id);
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id ? { ...i, checked: next, checked_at: next ? new Date().toISOString() : null } : i,
      ),
    );
    setSaving(false);
  };

  // ----------------------------------------------------------
  // 仮 → 確定 / 確定 → 巻き戻し(原則 5)
  // ----------------------------------------------------------
  const transitionStatus = async (
    toStatus: ExtractionStatus,
    reason?: string,
  ): Promise<void> => {
    if (!ex || statusBusy) return;
    setStatusBusy(true);
    const newHistory: RollbackHistoryEntry[] = [
      ...(ex.rollback_history ?? []),
      {
        from_status: ex.status,
        to_status: toStatus,
        rolled_back_at: new Date().toISOString(),
        ...(reason ? { reason } : {}),
      },
    ];
    const { data, error } = await fieldDb()
      .from('extractions')
      .update({
        status: toStatus,
        rollback_history: newHistory,
        ...(toStatus === 'confirmed' ? { reviewed_at: new Date().toISOString() } : {}),
      })
      .eq('id', ex.id)
      .select()
      .single();
    setStatusBusy(false);
    if (error || !data) {
      console.error('status transition failed', error);
      return;
    }
    setEx(data as unknown as Extraction);
  };

  const handleConfirm = () => transitionStatus('confirmed');
  const handleRollback = async () => {
    const reason = window.prompt('巻き戻し理由(任意・空欄可)') ?? undefined;
    await transitionStatus('rolled_back', reason);
  };

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-start gap-2">
          <span
            className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[ex.status]}`}
          >
            {STATUS_LABEL[ex.status]}
          </span>
          <h1 className="min-w-0 flex-1 truncate text-sm font-semibold">
            {ex.building_name_text ?? '(無題)'}
          </h1>
        </div>
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
          {ex.order_number && <span>#{ex.order_number}</span>}
          {ex.due_date && (
            <span>
              {ex.due_date} {ex.due_time ?? ''}
            </span>
          )}
          {ex.confidence !== null && <span>信頼度 {(ex.confidence * 100).toFixed(0)}%</span>}
          <span className="uppercase">{ex.source_type}</span>
        </div>
      </header>

      {/* 確定 / 巻き戻しコントロール(原則 5) */}
      <section className="border-b border-slate-100 px-4 py-3">
        {ex.status === 'draft' && (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={statusBusy}
            className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <span className="material-symbols-outlined mr-1 text-base align-middle" aria-hidden="true">
              check
            </span>
            この内容で確定する
          </button>
        )}
        {ex.status === 'confirmed' && (
          <button
            type="button"
            onClick={handleRollback}
            disabled={statusBusy}
            className="w-full rounded-lg border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            <span className="material-symbols-outlined mr-1 text-base align-middle" aria-hidden="true">
              undo
            </span>
            仮案件に巻き戻す
          </button>
        )}
        {ex.status === 'rolled_back' && (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={statusBusy}
            className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <span className="material-symbols-outlined mr-1 text-base align-middle" aria-hidden="true">
              redo
            </span>
            再確定する
          </button>
        )}
      </section>

      {/* 巻き戻し履歴(原則 5: 誤確定対策で履歴を保持) */}
      {ex.rollback_history && ex.rollback_history.length > 0 && (
        <section className="border-b border-slate-100 bg-slate-50 px-4 py-3">
          <h2 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            状態履歴
          </h2>
          <ul className="space-y-0.5 text-[10px] text-slate-600">
            {ex.rollback_history.map((entry, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="font-mono">{entry.rolled_back_at.slice(0, 16).replace('T', ' ')}</span>
                <span>
                  {STATUS_LABEL[entry.from_status]} → {STATUS_LABEL[entry.to_status]}
                </span>
                {entry.reason && <span className="text-slate-500">({entry.reason})</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 受注 vs 実消費(原則: 板の弱点を補完・Phase 0.9) */}
      <ProfitSection profit={profit} unitOverride={partnerUnitOverride} />

      {/* 採寸セット(T8.10・原則 9-11) */}
      {measurementSets.length > 0 && (
        <MeasurementSetsSection sets={measurementSets} isMUnit={partnerUnitOverride?.['wallpaper_output'] === 'm'} />
      )}

      {/* PDF 本体を開く(ADR-0008 で原文ベタ表示を廃止し、こちらに置換) */}
      {pdfOrder && (
        <section className="border-b border-slate-100 px-4 py-4">
          <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            原本 PDF
          </h2>
          <button
            type="button"
            onClick={handleOpenPdf}
            disabled={pdfBusy}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base" aria-hidden="true">
              picture_as_pdf
            </span>
            {pdfBusy ? '署名 URL 生成中...' : 'PDF を開く'}
          </button>
          {pdfOrder.original_file_name && (
            <div className="mt-1 truncate text-[10px] text-slate-500">
              {pdfOrder.original_file_name}
            </div>
          )}
          {pdfError && (
            <div className="mt-2 rounded-md bg-rose-50 p-2 text-xs text-rose-700">{pdfError}</div>
          )}
        </section>
      )}

      {/* 明細(チェックリスト・原則 7) */}
      <section className="px-4 py-4">
        <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          明細・チェックリスト
        </h2>
        {items.length === 0 ? (
          <div className="text-xs text-slate-500">明細はありません</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li key={item.id} className="py-2">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggleItem(item)}
                    disabled={saving}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="text-sm">
                      {[item.product_text, item.location_text, item.category_text]
                        .filter(Boolean)
                        .join(' / ')}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      {item.quantity !== null && (
                        <span>
                          {item.quantity} {item.unit ?? ''}
                        </span>
                      )}
                      {item.notes && <span className="ml-2">— {item.notes}</span>}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 板送信ボタン(確定後のみ・原則 17) */}
      <section className="border-t border-slate-100 px-4 py-4">
        <button
          type="button"
          className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          disabled={ex.status !== 'confirmed' || ex.status === ('sent_to_provider' as ExtractionStatus)}
          title={ex.status !== 'confirmed' ? '確定後に送信できます' : ''}
        >
          {ex.status === ('sent_to_provider' as ExtractionStatus)
            ? `送信済 → ${ex.sent_to_provider ?? 'board'}`
            : 'プレビューして board へ送信'}
        </button>
        {ex.provider_url && (
          <a
            href={ex.provider_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 block text-center text-xs text-emerald-700 underline"
          >
            board で開く →
          </a>
        )}
      </section>
    </div>
  );
}

function MeasurementSetsSection({
  sets,
  isMUnit,
}: {
  sets: MeasurementSetRow[];
  isMUnit: boolean;
}) {
  const unitLabel = isMUnit ? 'm' : '㎡';
  return (
    <section className="border-b border-slate-100 px-4 py-4">
      <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        採寸セット
      </h2>
      <div className="space-y-3">
        {sets.map((ms) => {
          const sections = Array.from(new Set(ms.lines.map((l) => l.section_index))).sort((a, b) => a - b);
          const totalSqm = ms.lines.reduce((a, b) => a + b.area_sqm, 0);
          const totalDisplay = isMUnit ? totalSqm / CLOTH_WIDTH_M : totalSqm;
          return (
            <div key={ms.id} className="rounded-md border border-slate-200 bg-white p-2 text-xs">
              <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px] text-slate-500">
                <span>{ms.received_at.slice(0, 16).replace('T', ' ')}</span>
                <span className="font-mono">
                  計 {totalDisplay.toFixed(2)} {unitLabel}
                </span>
              </div>
              {sections.map((s) => {
                const lines = ms.lines.filter((l) => l.section_index === s);
                if (lines.length === 0) return null;
                const sum = lines.reduce((a, b) => a + b.area_sqm, 0);
                const sumDisplay = isMUnit ? sum / CLOTH_WIDTH_M : sum;
                const first = lines[0]!;
                return (
                  <div key={s} className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]">
                    <span className="font-semibold text-slate-700">
                      {first.room_label ?? `部屋${s + 1}`}
                    </span>
                    {first.part_name && (
                      <span className="rounded bg-slate-100 px-1 text-[9px] text-slate-600">
                        {first.part_name}
                      </span>
                    )}
                    <span className="font-mono text-slate-500">
                      {lines
                        .map((l) => `${l.height_cm}${l.column_count > 1 ? `-${l.column_count}` : ''}`)
                        .join(', ')}
                    </span>
                    <span className="ml-auto font-mono text-slate-600">
                      {sumDisplay.toFixed(2)} {unitLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProfitSection({
  profit,
  unitOverride,
}: {
  profit: ProfitRow | null;
  unitOverride: Record<string, string> | null;
}) {
  if (!profit) return null;

  // 表示単位の決定(原則 13: 取引先別単位)
  const isMUnit =
    unitOverride?.wallpaper_output === 'm' || profit.ordered_unit === 'm';
  const unitLabel = isMUnit ? 'm' : '㎡';

  const ordered = profit.ordered_quantity;
  // measurement_lines.area_sqm は常に ㎡。アクシス案件は m 換算(÷ クロス幅 0.9m)
  const consumed = isMUnit ? profit.consumed_sqm / CLOTH_WIDTH_M : profit.consumed_sqm;

  const diff = consumed - ordered;
  const diffRate = ordered > 0 ? (diff / ordered) * 100 : null;

  const hasOrder = ordered > 0;
  const hasConsumed = profit.measurement_line_count > 0;

  return (
    <section className="border-b border-slate-100 bg-gradient-to-br from-emerald-50 to-sky-50 px-4 py-4">
      <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
        受注 vs 実消費
      </h2>
      <div className="grid grid-cols-3 gap-2">
        <Metric label="受注" value={hasOrder ? ordered : null} unit={unitLabel} tone="slate" />
        <Metric
          label="実消費"
          value={hasConsumed ? consumed : null}
          unit={unitLabel}
          tone="slate"
        />
        <Metric
          label="差"
          value={hasOrder && hasConsumed ? diff : null}
          unit={unitLabel}
          tone={diff > 0 ? 'rose' : 'emerald'}
          showSign
          {...(diffRate !== null
            ? { secondary: `${diffRate >= 0 ? '+' : ''}${diffRate.toFixed(1)}%` }
            : {})}
        />
      </div>
      {!hasOrder && (
        <div className="mt-2 text-[10px] text-slate-500">受注明細(クロス系)がまだ抽出されていません</div>
      )}
      {!hasConsumed && (
        <div className="mt-1 text-[10px] text-slate-500">
          実消費(採寸)がまだ紐付いていません — 採寸ページで案件に紐付けてください
        </div>
      )}
    </section>
  );
}

function Metric({
  label,
  value,
  unit,
  tone,
  showSign,
  secondary,
}: {
  label: string;
  value: number | null;
  unit: string;
  tone: 'slate' | 'emerald' | 'rose';
  showSign?: boolean;
  secondary?: string;
}) {
  const toneClasses = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
  }[tone];
  const display =
    value === null
      ? '—'
      : `${showSign && value > 0 ? '+' : ''}${value.toFixed(2)}`;
  return (
    <div className="rounded-md bg-white px-2 py-2 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm ${toneClasses}`}>
        {display}
        {value !== null && <span className="ml-0.5 text-[10px] text-slate-500">{unit}</span>}
      </div>
      {secondary && <div className="text-[10px] text-slate-500">{secondary}</div>}
    </div>
  );
}

// ADR-0008 で原文ベタ表示を廃止したため、SpansRendered / LegendItem は撤去。
// span 色定義(utils/spans.ts)+ ExtractedSpan 型は将来の再利用用にそのまま残置。
